// Date/time helpers shared by the day-2 scheduler and the bulk-import preview.
//
// Game `time` is stored as a human string ("10:00 AM" or "13:00").
// Game `globalDate` is stored as "M-DD-YYYY".
// The calendar logic prefers a normalized "YYYY-MM-DD" date key internally.

export const parseTimeToMinutes = (timeString: string | null | undefined): number => {
  if (!timeString) return 0;

  const twelve = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelve) {
    let hours = parseInt(twelve[1], 10);
    const minutes = parseInt(twelve[2], 10);
    const period = twelve[3].toUpperCase();
    if (period === 'AM' && hours === 12) hours = 0;
    if (period === 'PM' && hours !== 12) hours += 12;
    return hours * 60 + minutes;
  }

  const twentyFour = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFour) {
    return parseInt(twentyFour[1], 10) * 60 + parseInt(twentyFour[2], 10);
  }

  return 0;
};

export const isParseableTime = (timeString: string | null | undefined): boolean => {
  if (!timeString) return false;
  return /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(timeString) || /^(\d{1,2}):(\d{2})$/.test(timeString);
};

export const formatMinutesForGameTime = (minutes: number): string => {
  const h24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${mins.toString().padStart(2, '0')} ${ampm}`;
};

export const formatTimeLabel = (minutes: number): string => {
  const h24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return mins === 0 ? `${h12} ${ampm}` : `${h12}:${mins.toString().padStart(2, '0')} ${ampm}`;
};

export const toDateKey = (raw: string | Date | null | undefined): string => {
  if (!raw) return '';
  const asString = typeof raw === 'string' ? raw : raw.toISOString();

  const mdY = asString.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (mdY) {
    const month = mdY[1].padStart(2, '0');
    const day = mdY[2].padStart(2, '0');
    return `${mdY[3]}-${month}-${day}`;
  }

  // A bare "YYYY-MM-DD" is already a date key — return it verbatim. It MUST NOT
  // reach the Date fallback below: JS parses date-only ISO strings as UTC
  // midnight, and `.getDate()` then reads them in LOCAL time, so for any admin
  // west of UTC every date shifted back a day ("2026-08-15" → "2026-08-14").
  // Caught during the 2026 Nikkei Games bulk import, where all 848 games
  // previewed on the wrong day — in the format the tool's own error message
  // recommends.
  const iso = asString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  }

  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return '';

  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const toGlobalDate = (dateKey: string): string => {
  if (!dateKey) return '';
  const [yyyy, mm, dd] = dateKey.split('-');
  return `${mm}-${dd}-${yyyy}`;
};

// ── Timezone-correct instants ─────────────────────────────────────────────
// Game.date is NOT a reliable instant: every admin scheduler writes the
// calendar STRING into it (Mongoose casts to midnight in the server's local
// zone) — only `globalDate` + `time` carry the real wall-clock. Anything that
// needs a true instant (calendar feeds, the demo-refresh roll) must rebuild
// it in an explicit IANA timezone with the helpers below (DST-safe, Intl-based).

/** Offset (ms) of `tz` from UTC at the given instant. */
export const tzOffsetMs = (tz: string, atMs: number): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, number> = {};
  for (const p of dtf.formatToParts(new Date(atMs))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return wallAsUtc - Math.floor(atMs / 1000) * 1000;
};

/** UTC instant for a wall-clock (y, m 1-based, d, minutes-since-midnight) in `tz` (two-pass). */
export const instantFor = (y: number, m: number, d: number, minutes: number, tz: string): Date => {
  let guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  for (let i = 0; i < 2; i++) {
    guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60) - tzOffsetMs(tz, guess);
  }
  return new Date(guess);
};
